import type { ResearchArchiveEntry, ResearchPlan, ResearchTopicId } from '@/game/types'
import { researchSpent } from '../game/research'

interface ResearchScreenProps {
  plan: ResearchPlan
  archive: ResearchArchiveEntry[]
  available: number
  maximum: number
  weeklyGeneration: number
  opponentStyle: string
  opponentAssessment: string
  onToggle: (topicId: ResearchTopicId) => void
  onRestore: () => void
  onBack: () => void
  onContinue: () => void
}

export function ResearchScreen({
  plan,
  archive,
  available,
  maximum,
  weeklyGeneration,
  opponentStyle,
  opponentAssessment,
  onToggle,
  onRestore,
  onBack,
  onContinue,
}: ResearchScreenProps) {
  const spent = researchSpent(plan)
  const selected = plan.topics.filter(topic => topic.isSelected)

  return (
    <div className="screen research-screen">
      <button className="back-link" type="button" onClick={onBack}>Back to dashboard</button>
      <div className="page-title research-title">
        <div>
          <span className="eyebrow">Football intelligence — Round {plan.round}</span>
          <h1>Opposition Research</h1>
          <p>The staff report is free. Spend capacity to replace broad assumptions with actionable, persistent knowledge.</p>
        </div>
        <div className="research-title-actions">
          <span className={`status-pill ${selected.length ? 'override' : ''}`}>{selected.length ? `${selected.length} investigation${selected.length === 1 ? '' : 's'}` : 'Staff report only'}</span>
          <button className="text-button" type="button" disabled={!selected.length} onClick={onRestore}>{selected.length ? 'Restore Staff Research' : 'Staff Research Active'}</button>
        </div>
      </div>

      <section className="panel research-ledger" aria-label="Research capacity ledger">
        <div><span>Opening reserve</span><strong>{plan.openingCapacity}</strong></div>
        <b>−</b>
        <div><span>Committed</span><strong>{spent}</strong></div>
        <b>=</b>
        <div className="research-balance"><span>Projected balance</span><strong>{available}/{maximum}</strong></div>
        <p>Unspent capacity carries forward to the {maximum}-point cap. The department generates +{weeklyGeneration} after the match. Knowledge persists until its subject changes.</p>
      </section>

      <section className="panel baseline-report">
        <div><span className="eyebrow">Included — no spend</span><h2>Staff baseline report</h2></div>
        <span className="confidence low">Low confidence</span>
        <p><strong>Observed style:</strong> {opponentStyle}</p>
        <p>{opponentAssessment}</p>
        <small>Broad enough to prepare a competent staff plan; not precise enough to confirm triggers or exploitable patterns.</small>
      </section>

      <section className="research-grid" aria-label="Research investigations">
        {plan.topics.map(topic => {
          const affordable = topic.isSelected || available >= topic.cost
          return (
            <article className={`panel research-card ${topic.isSelected ? 'researched' : ''}`} key={topic.id}>
              <div className="research-card-head">
                <div><span className="eyebrow">{topic.domain}</span><h2>{topic.title}</h2></div>
                <span className={`confidence ${topic.isSelected ? 'high' : 'low'}`}>{topic.isSelected ? 'High confidence' : 'Low confidence'}</span>
              </div>
              <p className="research-summary">{topic.isSelected ? topic.revealedSummary : topic.baselineSummary}</p>
              {topic.isSelected ? (
                <div className="research-reveal">
                  <strong>Actionable finding</strong>
                  <p>{topic.tacticalAdvice}</p>
                  <small>{topic.persistence}</small>
                </div>
              ) : (
                <div className="research-unknown"><strong>Investigation will reveal</strong><span>A specific behaviour, confidence upgrade and linked tactical response.</span></div>
              )}
              <button
                className={topic.isSelected ? 'restore' : 'primary-action'}
                type="button"
                disabled={!affordable}
                onClick={() => onToggle(topic.id)}
                aria-label={topic.isSelected ? `Remove ${topic.title} investigation and refund ${topic.cost} research points` : `Investigate ${topic.title} for ${topic.cost} research points`}
              >
                {topic.isSelected ? `Remove — refund ${topic.cost}` : affordable ? `Investigate — ${topic.cost} pts` : `Need ${topic.cost} pts`}
              </button>
            </article>
          )
        })}
      </section>

      {archive.length > 0 && <section className="panel research-archive"><div className="section-heading"><div><span className="eyebrow">Persistent knowledge</span><h2>Opposition archive</h2></div><span className="status-pill">Historical</span></div>{archive.map((entry, index) => <div className="archive-entry" key={`${entry.opponent}-${entry.domain}-${index}`}><strong>{entry.opponent} — {entry.domain}</strong><span>Round {entry.round} · {entry.confidence} confidence · {entry.status}</span><p>{entry.summary}</p></div>)}</section>}

      <div className="sticky-actions"><span>{selected.length ? `${spent} research committed · ${available} carries forward` : 'No spend · full reserve carries forward'}</span><button className="primary-action" type="button" onClick={onContinue}>{selected.length ? 'Save Research Plan — Return to Week' : 'Use Staff Report — Return to Week'}</button></div>
    </div>
  )
}
